<script lang="ts" setup>
	/**
	 * Barre d'application et menu latéral.
	 *
	 * Ce composant ne contient **pas** le contenu des pages : il n'expose aucun
	 * slot. Envelopper `<router-view>` dans le slot d'un composant intermédiaire
	 * empêchait Vue de le re-rendre au changement de route — l'URL changeait, mais
	 * l'écran restait le précédent. La navigation appartient donc à `App.vue`.
	 *
	 * En mode borne, rien de tout cela n'est affiché : l'écran est en libre-service
	 * face au public et ne doit exposer aucun chemin vers un compte ou un réglage.
	 */
	import { computed } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { useI18n } from 'vue-i18n';
	import { AppMode, Right } from '@taxi/shared';
	import {
		driverStatusPresentation,
		useIsGranted,
		useTokenStore,
		useUserStore,
	} from '@common/index';
	import { useAppModeStore } from '@/stores/AppModeStore';
	import { useDriverStore } from '@/stores/DriverStore';
	import MonacoLogo from './MonacoLogo.vue';
	import LanguageMenu from './LanguageMenu.vue';

	const route = useRoute();
	const router = useRouter();
	const { t } = useI18n();
	const isGranted = useIsGranted();

	const appMode = useAppModeStore();
	const userStore = useUserStore();
	const tokenStore = useTokenStore();
	const driverStore = useDriverStore();

	const menuOpen = defineModel<boolean>('menu', { default: false });

	/** Le bouton retour n'apparaît que là où il a un sens : pas sur un accueil. */
	const showBack = computed(() =>
		!['home', 'driver-home'].includes(String(route.name ?? '')),
	);

	const canSwitchMode = computed(
		() => isGranted(Right.DRIVER_MODE) && userStore.isDriver && !appMode.isKiosk,
	);

	const statusColor = computed(() => driverStatusPresentation(driverStore.status).color);

	const toggleMode = (): void => {
		appMode.switchMode(appMode.isDriving ? AppMode.CLIENT : AppMode.DRIVER);
		void router.push({ name: appMode.isDriving ? 'driver-home' : 'home' });
		menuOpen.value = false;
	};

	const logout = async (): Promise<void> => {
		await userStore.logout();
		menuOpen.value = false;
		void router.push({ name: 'home' });
	};
</script>

<template>
	<!-- Borne : aucun élément de navigation, l'écran est en libre-service. -->
	<template v-if="!appMode.isKiosk">
		<v-app-bar flat density="comfortable" color="surface" class="app-shell_bar">
			<v-btn v-if="showBack" icon variant="text" @click="router.back()">
				<v-icon>mdi-arrow-left</v-icon>
			</v-btn>

			<router-link :to="{ name: appMode.isDriving ? 'driver-home' : 'home' }" class="app-shell_brand">
				<MonacoLogo compact />
			</router-link>

			<v-spacer />

			<!-- En mode conduite, la disponibilité est visible en permanence :
			     c'est l'information qui conditionne tout le reste. -->
			<v-chip
				v-if="appMode.isDriving && driverStore.profile"
				:color="statusColor"
				variant="flat"
				size="small"
				class="mr-2"
			>
				{{ t(`driver.status.${driverStore.status}`) }}
			</v-chip>

			<LanguageMenu />

			<v-btn icon variant="text" @click="menuOpen = true">
				<v-icon>mdi-menu</v-icon>
			</v-btn>
		</v-app-bar>

		<v-navigation-drawer v-model="menuOpen" location="right" temporary width="300">
			<div class="pa-4">
				<template v-if="userStore.me">
					<div class="d-flex align-center ga-3 mb-2">
						<v-avatar color="primary" size="44">
							<span class="text-h6">{{ userStore.initials }}</span>
						</v-avatar>
						<div class="overflow-hidden">
							<div class="font-weight-medium text-truncate">{{ userStore.displayName }}</div>
							<div class="text-caption text-medium-emphasis text-truncate">
								{{ userStore.me.email }}
							</div>
						</div>
					</div>
				</template>
				<div v-else class="text-medium-emphasis mb-2">
					{{ t('account.createAccountHint') }}
				</div>
			</div>

			<v-divider />

			<v-list nav density="comfortable">
				<!-- Bascule conduite ⇄ client : le chauffeur reste un client. -->
				<v-list-item
					v-if="canSwitchMode"
					:prepend-icon="appMode.isDriving ? 'mdi-account' : 'mdi-taxi'"
					:title="appMode.isDriving ? t('driver.riderMode') : t('driver.mode')"
					@click="toggleMode()"
				/>

				<v-list-item
					v-if="isGranted(Right.RIDE_READ_OWN)"
					prepend-icon="mdi-history"
					:title="t('ride.history')"
					:to="{ name: 'ride-history' }"
					@click="menuOpen = false"
				/>

				<v-list-item
					v-if="appMode.isDriving && isGranted(Right.DRIVER_SELF_READ)"
					prepend-icon="mdi-car-info"
					:title="t('driver.myProfile')"
					:to="{ name: 'driver-profile' }"
					@click="menuOpen = false"
				/>

				<v-list-item
					v-if="isGranted(Right.ACCOUNT_READ)"
					prepend-icon="mdi-account-cog"
					:title="t('account.myAccount')"
					:to="{ name: 'account' }"
					@click="menuOpen = false"
				/>
			</v-list>

			<template #append>
				<div class="pa-3">
					<!-- Une session anonyme n'a rien à déconnecter : on propose la
					     création de compte, qui est le vrai geste utile. -->
					<v-btn
						v-if="userStore.me && !tokenStore.isGuest"
						block
						variant="tonal"
						prepend-icon="mdi-logout"
						@click="logout()"
					>
						{{ t('actions.logout') }}
					</v-btn>
					<v-btn
						v-else
						block
						color="primary"
						variant="flat"
						prepend-icon="mdi-account-plus"
						:to="{ name: 'register' }"
						@click="menuOpen = false"
					>
						{{ t('actions.register') }}
					</v-btn>
				</div>
			</template>
		</v-navigation-drawer>
	</template>
</template>

<style lang="scss">
	.app-shell {
		&_bar {
			border-bottom: 1px solid rgb(var(--v-border-color));
		}

		&_brand {
			display: flex;
			align-items: center;
			text-decoration: none;
			color: rgb(var(--v-theme-primary));
		}
	}
</style>
